import { useState, useEffect, useRef, useCallback, CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, extractApiError } from '../api/client';

const DRAFT_KEY = 'sf_wizard_draft_v2';

interface PipelinePrefill {
  fromPipelineId?: string;
  linkedStartupId?: string;
  name?: string;
  description?: string;
  targetSegment?: string;
  problemStatement?: string;
  differentiator?: string;
}

const PROBLEM_CHIPS = [
  'It wastes too much time',
  'It costs too much money',
  'Existing tools are too complex',
  'The process is unreliable',
  'Teams struggle to collaborate',
  'Data is scattered everywhere',
];

const CUSTOMER_TYPES = [
  { id: 'B2C', label: 'Consumers', desc: 'Individual users', icon: '👤' },
  { id: 'B2B SMB', label: 'Small Business', desc: 'Teams of 1–50', icon: '🏢' },
  { id: 'B2B Enterprise', label: 'Enterprise', desc: 'Large corporations', icon: '🏗️' },
  { id: 'Marketplace', label: 'Marketplace', desc: 'Buyers & sellers', icon: '🛒' },
  { id: 'SaaS', label: 'SaaS', desc: 'Software companies', icon: '⚙️' },
  { id: 'Other', label: 'Other', desc: 'Something unique', icon: '✨' },
];

const CONFETTI_COLORS = ['#6366f1', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

interface WizardData {
  name: string;
  problemStatement: string;
  targetSegment: string;
  competitor: string;
  differentiator: string;
}

interface Particle {
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  rotation: number;
}

const INITIAL: WizardData = {
  name: '',
  problemStatement: '',
  targetSegment: '',
  competitor: '',
  differentiator: '',
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ color: '#6b7280', fontSize: 13, width: 90, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ color: '#e5e7eb', fontSize: 13, lineHeight: 1.5 }}>{value}</span>
    </div>
  );
}

function primaryBtn(disabled: boolean): CSSProperties {
  return {
    background: disabled
      ? 'rgba(99,102,241,0.25)'
      : 'linear-gradient(135deg, #6366f1, #a855f7)',
    color: disabled ? 'rgba(255,255,255,0.35)' : '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '14px 32px',
    fontSize: 15,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.2s',
    boxShadow: disabled ? 'none' : '0 4px 24px rgba(99,102,241,0.4)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
}

const backBtn: CSSProperties = {
  background: 'transparent',
  color: '#6b7280',
  border: '1px solid #2a2a3e',
  borderRadius: 12,
  padding: '14px 24px',
  fontSize: 15,
  cursor: 'pointer',
};

const inlineInput: CSSProperties = {
  display: 'inline-block',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid rgba(99,102,241,0.6)',
  color: '#e0e7ff',
  fontSize: 15,
  padding: '2px 6px',
  outline: 'none',
  minWidth: 130,
  textAlign: 'center',
};

export default function StartupWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { prefill?: PipelinePrefill } | null)?.prefill;
  const hasPrefill = !!(prefill?.fromPipelineId);

  const [step, setStep] = useState(1);
  const [dir, setDir] = useState<'fwd' | 'back'>('fwd');
  const [data, setData] = useState<WizardData>(() => {
    // If prefill from pipeline, skip draft and use prefill
    if (hasPrefill && prefill) {
      return {
        name: '',
        problemStatement: prefill.problemStatement ?? '',
        targetSegment: prefill.targetSegment ?? '',
        competitor: '',
        differentiator: prefill.differentiator ?? '',
      };
    }
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? { ...INITIAL, ...JSON.parse(saved) } : INITIAL;
    } catch {
      return INITIAL;
    }
  });
  const [hasDraft, setHasDraft] = useState(() => {
    if (hasPrefill) return false;
    try { return !!(localStorage.getItem(DRAFT_KEY)); } catch { return false; }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [createdId, setCreatedId] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [saveNameError, setSaveNameError] = useState('');
  const [particles, setParticles] = useState<Particle[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);

  // Lock body scroll while wizard is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // D1: Clear any saved draft when wizard is launched from pipeline
  useEffect(() => {
    if (hasPrefill) {
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus name input on step 1
  useEffect(() => {
    if (step === 1) setTimeout(() => nameRef.current?.focus(), 50);
  }, [step]);

  // Auto-save draft
  useEffect(() => {
    if (step < 5) {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(data)); } catch { /* ignore */ }
    }
  }, [data, step]);

  // Generate confetti when step 5 is reached
  useEffect(() => {
    if (step === 5) {
      setParticles(
        Array.from({ length: 60 }, (_, i) => ({
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * 2.5,
          duration: 2 + Math.random() * 3,
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          size: 6 + Math.floor(Math.random() * 8),
          rotation: Math.floor(Math.random() * 360),
        }))
      );
    }
  }, [step]);

  const set = useCallback((key: keyof WizardData, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const goNext = useCallback(() => { setDir('fwd'); setStep((s) => s + 1); }, []);
  const goBack = useCallback(() => { setDir('back'); setStep((s) => s - 1); }, []);

  const canProceed = () => {
    if (step === 1) return data.name.trim().length >= 3;
    if (step === 2) return data.problemStatement.trim().length >= 50;
    if (step === 3) return !!data.targetSegment;
    if (step === 4) return data.differentiator.trim().length >= 5;
    return true;
  };

  // D2: Save name directly to existing startup (when linkedStartupId in prefill)
  const handleSaveName = async () => {
    if (!prefill?.linkedStartupId || !data.name.trim()) return;
    setIsSavingName(true);
    setSaveNameError('');
    try {
      await api.put(`/startups/${prefill.linkedStartupId}`, { name: data.name.trim() });
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/startups/${prefill.linkedStartupId}`);
    } catch (err) {
      setSaveNameError(extractApiError(err).message);
      setIsSavingName(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setServerError('');
    try {
      const uvp = data.competitor
        ? `Unlike ${data.competitor}, we ${data.differentiator} for ${data.targetSegment} customers`
        : `${data.name} helps ${data.targetSegment} customers to ${data.differentiator}`;
      const description = data.name
        ? `${data.name} helps ${data.targetSegment} customers by offering: ${data.differentiator}.`
        : (prefill?.description ?? '');
      const { data: res } = await api.post('/startups', {
        name: data.name,
        description,
        targetSegment: data.targetSegment,
        problemStatement: data.problemStatement,
        uniqueValueProposition: uvp,
      });
      const newStartupId: string = res.data.startup.id;
      localStorage.removeItem(DRAFT_KEY);
      // CHANGE 8: link startup back to pipeline (fire-and-forget)
      if (prefill?.fromPipelineId) {
        api.patch(`/pipeline/${prefill.fromPipelineId}`, { linkedStartupId: newStartupId }).catch(() => {});
      }
      setCreatedId(newStartupId);
      goNext();
    } catch (err) {
      setServerError(extractApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPct = step >= 5 ? 100 : ((step - 1) / 4) * 100;
  const slideClass = dir === 'fwd' ? 'wizard-slide-fwd' : 'wizard-slide-back';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: '#0a0a16', overflowY: 'auto' }}>

      {/* Animated blobs */}
      <div aria-hidden style={{ position: 'absolute', top: '15%', left: '25%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)', filter: 'blur(100px)', borderRadius: '50%', animation: 'wizard-blob 10s ease-in-out infinite', pointerEvents: 'none' }} />
      <div aria-hidden style={{ position: 'absolute', bottom: '10%', right: '15%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(168,85,247,0.22) 0%, transparent 70%)', filter: 'blur(80px)', borderRadius: '50%', animation: 'wizard-blob 14s ease-in-out infinite reverse', pointerEvents: 'none' }} />

      {/* Confetti */}
      {particles.map((p) => (
        <div
          key={p.id}
          aria-hidden
          style={{
            position: 'fixed',
            top: -20,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: 2,
            transform: `rotate(${p.rotation}deg)`,
            animation: `wizard-confetti ${p.duration}s ${p.delay}s ease-in forwards`,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Progress bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: '#1e1e30', zIndex: 60 }}>
        <div style={{ height: '100%', background: 'linear-gradient(to right, #6366f1, #a855f7)', width: `${progressPct}%`, transition: 'width 500ms ease' }} />
      </div>

      {/* Close button */}
      {step < 5 && (
        <button
          onClick={() => navigate('/startups')}
          style={{ position: 'fixed', top: 16, right: 20, color: '#4b5563', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, zIndex: 60, lineHeight: 1 }}
          aria-label="Exit wizard"
        >
          ✕
        </button>
      )}

      {/* Pipeline prefill banner */}
      {hasPrefill && step < 5 && (
        <div style={{
          position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
          borderRadius: 10, padding: '7px 18px', color: '#a5b4fc', fontSize: 12,
          whiteSpace: 'nowrap', zIndex: 60, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>⚡</span>
          <span>Pre-filled from your pipeline blueprint</span>
        </div>
      )}

      {/* Draft restored toast */}
      {hasDraft && step === 1 && data.name && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 10, padding: '10px 20px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap', zIndex: 60, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Draft restored — <strong style={{ color: '#e5e7eb' }}>{data.name}</strong></span>
          <button
            onClick={() => { setData(INITIAL); setHasDraft(false); try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }}
            style={{ color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
          >
            Start fresh
          </button>
        </div>
      )}

      {/* Main content */}
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', position: 'relative' }}>
        <div key={step} className={slideClass} style={{ width: '100%', maxWidth: 580 }}>

          {/* ── Step 1 (D2): Name Your Startup — when already linked from pipeline */}
          {step === 1 && prefill?.linkedStartupId && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#4b5563', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Name Your Startup</p>
              <h1 style={{ fontSize: 'clamp(32px,5vw,48px)', fontWeight: 800, color: '#fff', marginBottom: 16, lineHeight: 1.15 }}>
                Give your startup a name
              </h1>
              <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
                Your idea has been saved. Add a real name to complete setup.
              </p>
              <input
                ref={nameRef}
                type="text"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canProceed() && !isSavingName) handleSaveName(); }}
                placeholder="e.g. InvoiceZap"
                maxLength={100}
                autoComplete="off"
                style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '2px solid rgba(99,102,241,0.4)', color: '#fff', fontSize: 32, fontWeight: 700, textAlign: 'center', padding: '8px 0 12px', outline: 'none', marginBottom: 12 }}
                onFocus={(e) => { e.currentTarget.style.borderBottomColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderBottomColor = 'rgba(99,102,241,0.4)'; }}
              />
              {data.name.length > 0 && data.name.length < 3 && (
                <p style={{ color: '#f87171', fontSize: 13, marginBottom: 4 }}>At least 3 characters</p>
              )}
              {saveNameError && (
                <p style={{ color: '#f87171', fontSize: 13, marginBottom: 4 }}>{saveNameError}</p>
              )}
              <div style={{ height: 44 }} />
              <button onClick={handleSaveName} disabled={!canProceed() || isSavingName} style={primaryBtn(!canProceed() || isSavingName)}>
                {isSavingName ? 'Saving…' : 'Save Name →'}
              </button>
            </div>
          )}

          {/* ── Step 1: Name ───────────────────────────────────── */}
          {step === 1 && !prefill?.linkedStartupId && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#4b5563', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Step 1 of 4</p>
              <h1 style={{ fontSize: 'clamp(32px,5vw,48px)', fontWeight: 800, color: '#fff', marginBottom: 52, lineHeight: 1.15 }}>
                What's your startup called?
              </h1>
              <input
                ref={nameRef}
                type="text"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canProceed()) goNext(); }}
                placeholder="e.g. InvoiceZap"
                maxLength={100}
                autoComplete="off"
                style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '2px solid rgba(99,102,241,0.4)', color: '#fff', fontSize: 32, fontWeight: 700, textAlign: 'center', padding: '8px 0 12px', outline: 'none', marginBottom: 12 }}
                onFocus={(e) => { e.currentTarget.style.borderBottomColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderBottomColor = 'rgba(99,102,241,0.4)'; }}
              />
              {data.name.length > 0 && data.name.length < 3 && (
                <p style={{ color: '#f87171', fontSize: 13, marginBottom: 4 }}>At least 3 characters</p>
              )}
              <div style={{ height: 44 }} />
              <button onClick={goNext} disabled={!canProceed()} style={primaryBtn(!canProceed())}>
                Continue →
              </button>
            </div>
          )}

          {/* ── Step 2: Problem ────────────────────────────────── */}
          {step === 2 && (
            <div>
              <p style={{ color: '#4b5563', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Step 2 of 4</p>
              <h1 style={{ fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, color: '#fff', marginBottom: 10, lineHeight: 1.2 }}>
                What problem does <span style={{ color: '#818cf8' }}>{data.name}</span> solve?
              </h1>
              <p style={{ color: '#6b7280', marginBottom: 28, fontSize: 15 }}>Describe the pain your customers experience every day.</p>

              {/* Quick chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                {PROBLEM_CHIPS.map((chip) => {
                  const active = data.problemStatement.includes(chip);
                  return (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => {
                        if (active) {
                          set('problemStatement', data.problemStatement.replace(chip, '').replace(/\s{2,}/g, ' ').trim());
                        } else {
                          set('problemStatement', data.problemStatement ? `${data.problemStatement} ${chip}` : chip);
                        }
                      }}
                      style={{ padding: '6px 14px', borderRadius: 999, border: `1px solid ${active ? '#6366f1' : '#2a2a3e'}`, background: active ? 'rgba(99,102,241,0.15)' : 'transparent', color: active ? '#a5b4fc' : '#9ca3af', fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>

              <textarea
                value={data.problemStatement}
                onChange={(e) => set('problemStatement', e.target.value)}
                placeholder="Describe the specific problem your customers face. The more concrete, the better..."
                rows={5}
                style={{ width: '100%', background: 'rgba(255,255,255,0.025)', border: '1px solid #2a2a3e', borderRadius: 12, color: '#e5e7eb', fontSize: 15, padding: '14px 16px', resize: 'none', outline: 'none', lineHeight: 1.65, marginBottom: 8 }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#2a2a3e'; }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32, fontSize: 12, color: '#6b7280' }}>
                <span>{data.problemStatement.trim().split(/\s+/).filter(Boolean).length} words</span>
                <span style={{ color: data.problemStatement.length >= 50 ? '#34d399' : '#6b7280' }}>
                  {data.problemStatement.length >= 50 ? '✓ Looks good' : `${50 - data.problemStatement.length} more chars needed`}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={goBack} style={backBtn}>← Back</button>
                <button onClick={goNext} disabled={!canProceed()} style={primaryBtn(!canProceed())}>Continue →</button>
              </div>
            </div>
          )}

          {/* ── Step 3: Customer Type ──────────────────────────── */}
          {step === 3 && (
            <div>
              <p style={{ color: '#4b5563', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Step 3 of 4</p>
              <h1 style={{ fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, color: '#fff', marginBottom: 10, lineHeight: 1.2 }}>
                Who is your primary customer?
              </h1>
              <p style={{ color: '#6b7280', marginBottom: 32, fontSize: 15 }}>Select the segment that best describes {data.name}'s users.</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 40 }}>
                {CUSTOMER_TYPES.map((ct) => {
                  const selected = data.targetSegment === ct.id;
                  return (
                    <button
                      key={ct.id}
                      type="button"
                      onClick={() => set('targetSegment', ct.id)}
                      style={{ background: selected ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.02)', border: `1.5px solid ${selected ? '#6366f1' : '#2a2a3e'}`, borderRadius: 12, padding: '18px 16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', boxShadow: selected ? '0 0 24px rgba(99,102,241,0.2)' : 'none' }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 6 }}>{ct.icon}</div>
                      <div style={{ color: selected ? '#e0e7ff' : '#d1d5db', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{ct.label}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{ct.desc}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={goBack} style={backBtn}>← Back</button>
                <button onClick={goNext} disabled={!canProceed()} style={primaryBtn(!canProceed())}>Continue →</button>
              </div>
            </div>
          )}

          {/* ── Step 4: Differentiation ────────────────────────── */}
          {step === 4 && (
            <div>
              <p style={{ color: '#4b5563', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Step 4 of 4</p>
              <h1 style={{ fontSize: 'clamp(24px,4vw,36px)', fontWeight: 800, color: '#fff', marginBottom: 10, lineHeight: 1.2 }}>
                What makes <span style={{ color: '#818cf8' }}>{data.name}</span> different?
              </h1>
              <p style={{ color: '#6b7280', marginBottom: hasPrefill ? 12 : 40, fontSize: 15 }}>Complete the sentence to define your competitive edge.</p>
              {hasPrefill && (
                <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '8px 14px', marginBottom: 24, fontSize: 12, color: '#818cf8' }}>
                  ⚡ From Blueprint — review and adjust if needed
                </div>
              )}

              <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid #2a2a3e', borderRadius: 16, padding: '28px 24px', marginBottom: 40 }}>
                <p style={{ color: '#9ca3af', fontSize: 15, lineHeight: 2.4, margin: 0 }}>
                  Unlike{' '}
                  <input
                    type="text"
                    value={data.competitor}
                    onChange={(e) => set('competitor', e.target.value)}
                    placeholder="competitor"
                    style={inlineInput}
                  />
                  , {data.name} helps{' '}
                  <span style={{ color: '#818cf8' }}>{data.targetSegment}</span>
                  {' '}customers to{' '}
                  <input
                    type="text"
                    value={data.differentiator}
                    onChange={(e) => set('differentiator', e.target.value)}
                    placeholder="do X better / faster / cheaper"
                    style={{ ...inlineInput, minWidth: 220 }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && canProceed() && !isSubmitting) handleSubmit(); }}
                  />
                  .
                </p>
              </div>

              {serverError && (
                <p style={{ color: '#f87171', fontSize: 13, textAlign: 'center', marginBottom: 16 }}>{serverError}</p>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={goBack} style={backBtn} disabled={isSubmitting}>← Back</button>
                <button onClick={handleSubmit} disabled={!canProceed() || isSubmitting} style={primaryBtn(!canProceed() || isSubmitting)}>
                  {isSubmitting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    '🚀 Launch Startup'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 5: Celebration ────────────────────────────── */}
          {step === 5 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 72, marginBottom: 24, display: 'inline-block', animation: 'wizard-bounce 0.5s ease-out' }}>🚀</div>
              <h1 style={{ fontSize: 'clamp(32px,5vw,48px)', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
                {data.name} is live!
              </h1>
              <p style={{ color: '#9ca3af', fontSize: 16, marginBottom: 48, lineHeight: 1.7 }}>
                Your startup has been created. Time to build something the world needs.
              </p>

              {/* Summary card */}
              <div style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, padding: '24px', textAlign: 'left', marginBottom: 40 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <SummaryRow label="Name" value={data.name} />
                  <SummaryRow label="Customers" value={data.targetSegment} />
                  <SummaryRow
                    label="Edge"
                    value={data.competitor
                      ? `Unlike ${data.competitor}, we ${data.differentiator}`
                      : data.differentiator}
                  />
                  <SummaryRow
                    label="Problem"
                    value={data.problemStatement.length > 120 ? `${data.problemStatement.substring(0, 120)}…` : data.problemStatement}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <button onClick={() => navigate(`/startups/${createdId}`)} style={primaryBtn(false)}>
                  Launch Dashboard →
                </button>
                <button onClick={() => navigate(`/startups/${createdId}/financials`)} style={backBtn}>
                  Add financial projections
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
